import { pubsub, productLookUp, addToCartData } from '@services';
import { notyf } from '@modules';
import { debugError, debugLog } from '@utils';

class PicturatorConfigurator extends HTMLElement {
  static debug = false;

  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    if (!this.sectionId) {
      throw new Error('Section ID is required for configurator widget');
    }

    // Config
    this.configurationId = null;
    this.updateLineItem = false;

    // Cache locale from dataset or Shopify localization
    const locale =
      this.dataset.locale ||
      window.Shopify?.locale ||
      document.documentElement.lang?.substring(0, 2) ||
      'en';

    this.config = {
      containerId: `configurator-widget-${this.sectionId}`,
      shop: window.theme.shop || 'STIT',
      env: window.theme.env || 'production',
      campaign: window.theme.campaign.handle,
      campaignId: window.theme.campaign.handle || null,
      channel: window.theme.channel.handle || 'seo',
      tier: parseInt(window.theme.tier || 0),
      priceTier: parseInt(window.theme.tier || 0),
      locale: locale,
      showLanguageDropdown: false,
      priceSource: 'EXTERNAL',
    };
    try {
      this.config = { ...this.config, ...JSON.parse(this.dataset.config || '{}') };
    } catch (error) {
      debugError('Failed to parse configurator liquid config:', error);
      this.config = { ...this.config };
    }

    // Start prefetching campaign price tiers early (runs in parallel with widget loading)
    this._campaignTiersPromise = this.config.campaignId
      ? this.#fetchCampaignPriceTiers(this.config.campaignId)
      : Promise.resolve(null);

    // Cache DOM selectors
    this.section = this.closest('.shopify-section');
    this.backButton = this.section?.querySelector('[data-back-button]');
    this.heading = this.section?.querySelector('[data-heading]');
    this.container = null; // Will be set when needed

    // Cache bound event handlers to prevent memory leaks
    this._boundBackButtonClick = this.#onBackButtonClick.bind(this);
    this._boundConfigurationStarted = this.#onConfigurationStarted.bind(this);
    this._boundProductChanged = this.#onProductChanged.bind(this);
    this._boundConfigurationChanged = this.#onConfigurationChanged.bind(this);
    this._boundImageUploaded = this.#onImageUploaded.bind(this);
    this._boundBeforeAddToCart = this.#onBeforeAddToCart.bind(this);
    this._boundAddToCartClicked = this.#onAddToCartClicked.bind(this);
    this._boundError = this.#onError.bind(this);
  }

  /**
   * On initialize the configurator
   */
  connectedCallback() {
    // Handle bfcache restoration - reload to ensure fresh state
    this._boundPageShow = this.#onPageShow.bind(this);
    window.addEventListener('pageshow', this._boundPageShow);

    // Preload image ASAP before widget initialization
    this.#preloadImage();

    this.#initializeConfigurator();
  }

  /**
   * Handle pageshow event to detect bfcache restoration
   * @param {PageTransitionEvent} event
   */
  #onPageShow(event) {
    // If page was restored from bfcache, reload to ensure fresh state
    if (event.persisted) {
      window.location.reload();
    }
  }

  /**
   * Preload image from sessionStorage as early as possible
   */
  #preloadImage() {
    const urlParams = new URLSearchParams(window.location.search);
    const uploadKey = urlParams.get('uploadKey');

    if (!uploadKey) return;

    try {
      const storageKey = `upload-key-${uploadKey}`;
      const storedData = sessionStorage.getItem(storageKey);

      if (storedData && storedData.startsWith('http')) {
        // Get the preview URL with size=450
        let previewUrl = storedData;
        if (previewUrl.includes('size=')) {
          previewUrl = previewUrl.replace(/size=\d+/, 'size=450');
        } else {
          previewUrl = previewUrl + (previewUrl.includes('?') ? '&' : '?') + 'size=450';
        }

        // Create image preload with CORS support
        const img = new Image();
        img.crossOrigin = 'anonymous'; // Enable CORS for canvas usage
        img.src = previewUrl;

        // Also add link preload for highest priority with crossorigin
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = previewUrl;
        link.crossOrigin = 'anonymous'; // Enable CORS for preload
        document.head.appendChild(link);
      }
    } catch (e) {
      console.warn('⚠️ Failed to preload image:', e);
    }
  }

  /**
   * Fetch campaign price tiers from Storefront API (variant metafield cm_discount/volume)
   * @param {string} campaignHandle - The campaign handle to look up
   * @returns {Array|null} Array of price tier objects or null
   */
  async #fetchCampaignPriceTiers(campaignHandle) {
    if (!window.theme.storefront_api_token || !window.theme.app_scope) return null;

    const namespace = `${window.theme.app_scope}cm_discount`;

    // Scan products/variants for the campaign discount metafield.
    // Build a per-format tiers map keyed by "WxH" cm (e.g. "75x50").
    // Also keep a global tiers reference (first match) for backwards compat.
    let cursor = null;
    const maxPages = 50;
    let globalTiers = null;
    let label = null;
    const tiersPerFormat = {};
    const labelsPerFormat = {};
    const tiersPerMaterial = {};

    // Keep products(first) × variants(first) ≤ 1000 (Storefront API query cost limit).
    // Shopify allows max 100 variants per product, so first:100 guarantees all variants.
    for (let page = 0; page < maxPages; page++) {
      const afterClause = cursor ? `, after: "${cursor}"` : '';
      const query = `
        query GetDiscountTiers {
          products(first: 10${afterClause}) {
            edges {
              node {
                variants(first: 100) {
                  edges {
                    node {
                      title
                      sku
                      metafield(namespace: "${namespace}", key: "volume") {
                        value
                      }
                    }
                  }
                }
              }
              cursor
            }
            pageInfo { hasNextPage }
          }
        }
      `;

      const response = await fetch(`/api/2025-07/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': window.theme.storefront_api_token,
        },
        body: JSON.stringify({ query }),
      });

      if (!response.ok) return null;

      const data = await response.json();
      if (data?.errors?.length) {
        console.warn('⚠️ Campaign GraphQL error:', data.errors[0]?.message);
        return null;
      }
      const products = data?.data?.products;
      if (!products?.edges?.length) break;

      for (const productEdge of products.edges) {
        for (const variantEdge of productEdge.node?.variants?.edges || []) {
          const metafieldValue = variantEdge.node?.metafield?.value;
          if (!metafieldValue) continue;

          try {
            const parsed = JSON.parse(metafieldValue);
            if (!parsed?.discounts?.length) continue;

            const discount = parsed.discounts.find((d) => d.cmp === campaignHandle);
            if (!discount?.t?.length) continue;

            const tiers = discount.t.map((minQty, index) => ({
              minQty,
              type: discount.r,
              value: discount.d[index],
            }));

            // Store global tiers and label from first match (backwards compat)
            if (!globalTiers) {
              globalTiers = tiers;
              if (discount.n) label = discount.n;
            }

            // Extract format key from variant title (e.g. "75x50 cm" → "75x50")
            const variantTitle = variantEdge.node?.title || '';
            const variantSku = variantEdge.node?.sku || '';
            const nums = variantTitle.match(/(\d+)\s*[x×]\s*(\d+)/i);
            const formatKey = nums ? `${parseInt(nums[1], 10)}x${parseInt(nums[2], 10)}` : null;

            if (formatKey) {
              tiersPerFormat[formatKey] = tiers;
              if (discount.n) labelsPerFormat[formatKey] = discount.n;
            }

            // Material-specific tiers (SKU prefix e.g. "CVS", "ACR", "ALU")
            if (variantSku && formatKey) {
              const materialKey = variantSku.substring(0, 3);
              if (!tiersPerMaterial[materialKey]) tiersPerMaterial[materialKey] = {};
              tiersPerMaterial[materialKey][formatKey] = tiers;
            }
          } catch (_) {
            continue;
          }
        }
      }

      if (!products.pageInfo?.hasNextPage) break;
      cursor = products.edges[products.edges.length - 1].cursor;
    }

    if (!globalTiers) {
      console.warn(
        `⚠️ Campaign "${campaignHandle}": No matching discount tier found in any variant metafield`
      );
      return null;
    }

    const hasPerFormat = Object.keys(tiersPerFormat).length > 0;
    const hasPerFormatLabels = Object.keys(labelsPerFormat).length > 0;
    const hasPerMaterial = Object.keys(tiersPerMaterial).length > 0;

    // Only use per-format maps when tiers actually differ between formats.
    // If all formats have the same tiers (e.g. global 10% off), keep only globalTiers
    // so the widget applies the discount to ALL formats (including ones not in the map).
    let isPerFormatDistinct = false;
    if (hasPerFormat) {
      const globalStr = JSON.stringify(globalTiers);
      isPerFormatDistinct = Object.values(tiersPerFormat).some(
        (t) => JSON.stringify(t) !== globalStr
      );
    }
    let isLabelsDistinct = false;
    if (hasPerFormatLabels && label) {
      isLabelsDistinct = Object.values(labelsPerFormat).some((l) => l !== label);
    }

    // Per material: if all formats within a material have same tiers, add __global__ shortcut
    if (hasPerMaterial) {
      for (const [material, formats] of Object.entries(tiersPerMaterial)) {
        const values = Object.values(formats);
        if (values.length > 0) {
          const first = JSON.stringify(values[0]);
          if (values.every((v) => JSON.stringify(v) === first)) {
            tiersPerMaterial[material]['__global__'] = values[0];
          }
        }
      }
    }

    console.log(
      `✅ Campaign "${campaignHandle}": globalTiers =`,
      globalTiers,
      isPerFormatDistinct ? ', tiersPerFormat =' : '',
      isPerFormatDistinct ? tiersPerFormat : '',
      isLabelsDistinct ? ', labelsPerFormat =' : '',
      isLabelsDistinct ? labelsPerFormat : '',
      hasPerMaterial ? ', tiersPerMaterial =' : '',
      hasPerMaterial ? tiersPerMaterial : '',
      ', label =',
      label,
      hasPerFormat
        ? `(${Object.keys(tiersPerFormat).length} formats, ${isPerFormatDistinct ? 'distinct' : 'all same → global'})`
        : ''
    );

    return {
      tiers: globalTiers,
      tiersPerFormat: isPerFormatDistinct ? tiersPerFormat : null,
      labelsPerFormat: isLabelsDistinct ? labelsPerFormat : null,
      tiersPerMaterial: hasPerMaterial ? tiersPerMaterial : null,
      label,
    };
  }

  /**
   * Initialize the configurator widget
   */
  async #initializeConfigurator() {
    // Check if ConfiguratorWidget is available
    if (typeof window.ConfiguratorWidget === 'undefined') {
      setTimeout(() => this.#initializeConfigurator(), 50);
      return;
    }

    // Hide the liquid loader immediately when widget is ready
    this.#hideLoader();

    const config = {
      ...this.config,
      ...this.#initUrlParams(),
    };

    // Wait for campaign price tiers (fetch was started in constructor)
    if (config.campaignId && this._campaignTiersPromise) {
      try {
        const result = await this._campaignTiersPromise;
        if (result?.tiers) {
          config.campaignPriceTiers = result.tiers;
          if (result.tiersPerFormat) {
            config.campaignPriceTiersPerFormat = result.tiersPerFormat;
          }
          if (result.labelsPerFormat) {
            config.campaignLabelsPerFormat = result.labelsPerFormat;
          }
          if (result.tiersPerMaterial) {
            config.campaignTiersPerMaterial = result.tiersPerMaterial;
          }
          if (result.label) {
            config.campaignLabel = result.label;
          }
          console.log(
            '✅ Campaign price tiers loaded:',
            result.tiers,
            result.tiersPerFormat ? 'perFormat:' : '',
            result.tiersPerFormat || '',
            result.labelsPerFormat ? 'labelsPerFormat:' : '',
            result.labelsPerFormat || '',
            result.tiersPerMaterial ? 'perMaterial:' : '',
            result.tiersPerMaterial || '',
            'label:',
            result.label
          );
        }
      } catch (e) {
        console.warn('⚠️ Failed to load campaign price tiers:', e);
      }
    }

    // Initialize the configurator immediately
    PicturatorConfigurator.debug && debugLog('Picturator: Initialize configurator', config);
    this.configurator = window.ConfiguratorWidget.init(config);

    // NGE-6279: Fix hideProductDropdown hiding the upload button.
    // The widget wraps both the product selector AND the upload button in
    // invisible h-0 overflow-hidden. Use a MutationObserver to unwrap the
    // container so only the dropdown is hidden (via CSS in the section).
    if (config.hideProductDropdown) {
      this.#fixHideProductDropdown();
    }

    // Subscribe to upload image event
    this.unsubscribeUploadImage = pubsub.subscribe(`upload:image`, ({ key, preselect }) => {
      this.updateImage(key, preselect);
    });

    // Event listeners - use cached bound functions
    this.backButton?.addEventListener('click', this._boundBackButtonClick);
    this.configurator.on('configuration_started', this._boundConfigurationStarted);
    this.configurator.on('product_changed', this._boundProductChanged);
    this.configurator.on('configuration_changed', this._boundConfigurationChanged);
    this.configurator.on('image_uploaded', this._boundImageUploaded);
    this.configurator.on('before_add_to_cart', this._boundBeforeAddToCart);
    this.configurator.on('add_to_cart_clicked', this._boundAddToCartClicked);
    this.configurator.on('error', this._boundError);
  }

  /**
   * On disconnect the configurator
   */
  disconnectedCallback() {
    // Unsubscribe from pubsub
    if (this.unsubscribeUploadImage) {
      this.unsubscribeUploadImage();
    }

    // Remove pageshow listener
    if (this._boundPageShow) {
      window.removeEventListener('pageshow', this._boundPageShow);
    }

    // Remove event listeners using cached bound references
    this.backButton?.removeEventListener('click', this._boundBackButtonClick);

    // Disconnect hideProductDropdown observer
    if (this._hideDropdownObserver) {
      this._hideDropdownObserver.disconnect();
      this._hideDropdownObserver = null;
    }

    if (!this.configurator) return;

    // Remove configurator event listeners
    this.configurator.off('configuration_started', this._boundConfigurationStarted);
    this.configurator.off('product_changed', this._boundProductChanged);
    this.configurator.off('configuration_changed', this._boundConfigurationChanged);
    this.configurator.off('image_uploaded', this._boundImageUploaded);
    this.configurator.off('before_add_to_cart', this._boundBeforeAddToCart);
    this.configurator.off('add_to_cart_clicked', this._boundAddToCartClicked);
    this.configurator.off('error', this._boundError);

    // Destroy the configurator
    this.configurator.destroy();

    // Reset references
    this.configurator = null;
    this.container = null;
  }

  /**
   * Init SKU-based preselection, configurationId, uploadKey based on URL params
   */
  #initUrlParams() {
    const urlParams = new URLSearchParams(window.location.search);

    // Get the upload key
    const uploadKey = urlParams.get('uploadKey');

    // Get the design ID
    this.configurationId = urlParams.get('configurationId');
    if (this.configurationId) {
      this.updateLineItem = true;
    }

    // Get the SKU preselection based on PM, F, MT, BM, FS, FN, BE, B
    const PM = urlParams.get('PM');
    const F = urlParams.get('F');
    const MT = urlParams.get('MT');
    const BM = urlParams.get('BM');
    const FS = urlParams.get('FS');
    const BE = urlParams.get('BE');
    const FN = urlParams.get('FN');
    const B = urlParams.get('B');
    const imageEffect = urlParams.get('imageEffect');

    // Check sessionStorage for preview URL to avoid loading full S3 image
    let downloadUrl = null;
    if (uploadKey) {
      try {
        const storageKey = `upload-key-${uploadKey}`;
        const storedData = sessionStorage.getItem(storageKey);

        if (storedData) {
          // Check if it's a plain URL string or JSON object
          if (storedData.startsWith('http')) {
            // Plain URL string - use it directly
            const previewUrl = storedData;

            // Replace size parameter if present, or add it
            if (previewUrl.includes('size=')) {
              downloadUrl = previewUrl.replace(/size=\d+/, 'size=450');
            } else {
              downloadUrl = previewUrl + (previewUrl.includes('?') ? '&' : '?') + 'size=450';
            }

            PicturatorConfigurator.debug &&
              debugLog('Found preview URL string in sessionStorage:', downloadUrl);
          } else {
            // Try to parse as JSON
            const parsedData = JSON.parse(storedData);

            // Prefer previewThumbnail if available (it's the smaller preview image)
            if (parsedData.previewThumbnail) {
              // Use preview URL with size=450 for better quality
              const previewUrl = parsedData.previewThumbnail;

              // Replace size parameter if present, or add it
              if (previewUrl.includes('size=')) {
                downloadUrl = previewUrl.replace(/size=\d+/, 'size=450');
              } else {
                downloadUrl = previewUrl + (previewUrl.includes('?') ? '&' : '?') + 'size=450';
              }

              PicturatorConfigurator.debug &&
                debugLog('Found preview URL in sessionStorage JSON:', downloadUrl);
            } else if (parsedData.downloadUrl) {
              // Fallback to downloadUrl if no previewThumbnail
              downloadUrl = parsedData.downloadUrl;
              PicturatorConfigurator.debug &&
                debugLog('Found download URL in sessionStorage JSON:', downloadUrl);
            }
          }
        }
      } catch (e) {
        console.warn('⚠️ Failed to parse sessionStorage data for uploadKey:', uploadKey, e);
      }
    }

    // Build the config object
    const config = {
      ...(uploadKey && { uploadKey }),
      ...(downloadUrl && { downloadUrl }), // Pass preview URL directly to avoid async state timing issues
      ...(this.configurationId && { itemId: this.configurationId }),
      ...(PM && { PM }),
      ...(F && { F }),
      ...(MT && { MT }),
      ...(BM && { BM }),
      ...(FS && { FS }),
      ...(BE && { BE }),
      ...(FN && { FN }),
      ...(B && { B }),
      ...(imageEffect && { imageEffect }),
    };

    // If no URL params are present, use the default preselection
    // Note: only PM is preselected — leaving F unset lets the configurator
    // auto-select the largest format compatible with the uploaded image's DPI,
    // matching legacy behaviour. Hard-coding F here forced the smallest format.
    if (Object.keys(config).length === 0) {
      return {
        PM: 'CVS',
      };
    }

    // Return the config object
    return config;
  }

  /**
   * Handle configuration started event
   * @param {*} data
   */
  #onConfigurationStarted(data) {
    PicturatorConfigurator.debug && debugLog('Picturator: Configuration Started', data);
    this.updateTitle(data.productName);
    this.#markConfiguratorReady();
  }

  /**
   * Hide the Shopify liquid loader
   */
  #hideLoader() {
    // Cache container reference
    if (!this.container) {
      this.container = document.getElementById(`configurator-widget-${this.sectionId}`);
    }

    if (this.container) {
      const loader = this.container.querySelector('.data-loader');
      if (loader) {
        loader.style.display = 'none';
        debugLog('Picturator: Hid liquid loader');
      }
    }
  }

  /**
   * NGE-6279: Fix hideProductDropdown hiding the upload button.
   * The widget applies invisible/h-0/overflow-hidden to a wrapper that contains
   * both the product selector AND the upload button. This observer strips those
   * classes from wrappers that contain .productSelector, so the upload button
   * stays visible. The product dropdown itself is hidden via CSS in the section.
   */
  #fixHideProductDropdown() {
    if (!this.container) {
      this.container = document.getElementById(`configurator-widget-${this.sectionId}`);
    }
    if (!this.container) return;

    const unwrapProductSelector = () => {
      const selectors = this.container.querySelectorAll('.productSelector');
      for (const selector of selectors) {
        // Walk up to find the invisible wrapper (max 3 levels)
        let el = selector.parentElement;
        for (let i = 0; i < 3 && el; i++) {
          if (el.classList.contains('invisible') && el.classList.contains('h-0')) {
            el.classList.remove('invisible', 'h-0', 'overflow-hidden');
            debugLog('Picturator: Unwrapped hideProductDropdown container');
          }
          el = el.parentElement;
        }
      }
    };

    // Run immediately in case the widget already rendered
    unwrapProductSelector();

    // Observe for React re-renders that may re-apply the classes
    this._hideDropdownObserver = new MutationObserver(unwrapProductSelector);
    this._hideDropdownObserver.observe(this.container, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true,
    });
  }

  /**
   * Mark configurator as ready (shows product dropdown)
   */
  #markConfiguratorReady() {
    // Cache container reference for reuse
    if (!this.container) {
      this.container = document.getElementById(`configurator-widget-${this.sectionId}`);
    }
    this.container?.classList.add('configurator-ready');
    // NGE-9205: release the CLS min-height reservation once the widget has mounted.
    // The reservation (2155/2660px, set via <style> before React renders) only holds
    // space during load; afterwards the widget root (.configurator-embed) defines its
    // own height, so a short/empty state left a large white gap below the widget.
    // Clearing the floor lets the container wrap the real content and grow naturally.
    if (this.container) {
      requestAnimationFrame(() => {
        this.container.style.minHeight = '0px';
      });
    }
  }

  /**
   * Handle product changed event
   * @param {*} data
   */
  #onProductChanged(data) {
    PicturatorConfigurator.debug && debugLog('Picturator: Product changed', data);
    this.updateTitle(data.productName);
  }

  /**
   * Handle configuration changed event
   * @param {*} data
   */
  #onConfigurationChanged(data) {
    PicturatorConfigurator.debug && debugLog('Picturator: Configuration Changed', data);
  }

  /**
   * Handle image uploaded event
   * @param {*} data
   */
  #onImageUploaded(data) {
    PicturatorConfigurator.debug && debugLog('Picturator: Image uploaded', data);
  }

  /**
   * Handle before add to cart event
   * @param {*} data
   */
  #onBeforeAddToCart(data) {
    PicturatorConfigurator.debug && debugLog('Picturator: Before add to cart', data);
    this.toggleLoading(true);

    // Start performance measurement
    this._perfStart = performance.now();

    // Pre-fetch variant data if SKU is available (runs in parallel with configurator Phase 1)
    if (data.sku?.PM && data.sku?.F) {
      this._prefetchKey = `${data.sku.PM}${data.sku.F}`;
      this._prefetchPromise = productLookUp.find(data.sku.PM, data.sku.F).catch(() => null);
    }
  }

  /**
   * Handle add to cart clicked event
   * @param {*} data
   */
  async #onAddToCartClicked(data) {
    PicturatorConfigurator.debug && debugLog('Picturator: Add to cart clicked', data);

    // SKU (add missing Border Effect(B) and Background Effect(BE))
    const sku = data.sku;
    if (data?.configurationApiResponse?.configuration?.borderEffect && !sku.B) {
      sku.B = data?.configurationApiResponse?.configuration?.borderEffect;
    }
    if (data?.configurationApiResponse?.configuration?.backgroundEffect && !sku.BE) {
      sku.BE = data?.configurationApiResponse?.configuration?.backgroundEffect;
    }

    // Get the configuration API response
    const configurationApiResponse = await data.configurationApiResponse;

    // Set the configuration ID if available
    let configurationId = data.configurationId;
    let previousConfigurationId = data.previousConfigurationId;
    if (this.updateLineItem && this.configurationId) {
      if (!configurationId) {
        configurationId = this.configurationId;
      }
      if (!previousConfigurationId) {
        previousConfigurationId = this.configurationId;
      }
    }

    // Prevent broken orders: configurationId is required for Picturator import
    if (!configurationId) {
      this.onError(
        new Error(
          window.toast_translations?.cart?.configuration_failed ||
            'Something went wrong with your configuration. Please try again or upload a new photo.'
        )
      );
      return;
    }

    // Config for the add to cart
    const config = {
      sku,
      quantity: data.quantity,
      configurationId,
      previousConfigurationId,
      uploadKey: data?.uploadKey || data?.uploadIntegration?.uploadKey,
      thumbnail: configurationApiResponse?.previewThumbnail ?? data?.thumbnailUrl ?? null,
      nextImage: data?.nextImage ?? null,
      imageEffect: data?.imageEffect || data?.selectedImageEffect || null,
    };
    this.addToCart(config);
  }

  /**
   * Handle error event
   * @param {*} data
   */
  #onError(data) {
    PicturatorConfigurator.debug && debugError('Picturator: Error', data);

    // Reset loading state in case before_add_to_cart fired but add_to_cart_clicked never will
    this.toggleLoading(false);
    this._perfStart = null;
    this._prefetchPromise = null;
    this._prefetchKey = null;
  }

  /**
   * Handle back button click event
   */
  #onBackButtonClick() {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/';
    }
  }

  /**
   * Update the configurator with a new configuration
   * @param {Object} config - Configuration object for the configurator
   */
  updateConfig(config) {
    if (!this.configurator) return;
    if (!config || typeof config !== 'object') return;
    PicturatorConfigurator.debug && debugLog('Picturator: Update config', config);

    this.configurator.update(config);
  }

  /**
   * Update the image in the configurator
   * @param {string} uploadKey
   * @param {Object} preselect - SKU preselection data
   */
  updateImage(uploadKey, preselect = {}) {
    if (!this.configurator) return;
    if (!uploadKey) return;
    PicturatorConfigurator.debug && debugLog('Picturator: Update image', uploadKey);

    // Update the configurator
    this.config = {
      ...this.config,
      ...preselect,
      uploadKey,
    };

    this.configurator.update(this.config);
  }

  /**
   * Update the title of the configurator
   * @param {string} productName - The product name to display in the heading
   */
  updateTitle(productName) {
    if (!productName) return;
    if (!this.heading) return;
    PicturatorConfigurator.debug && debugLog('Picturator: Update title', productName);

    // Plain text end to end: the template is a `text` section setting and the name comes from
    // the widget event, so neither is ever parsed as markup (NGE-14422). A function
    // replacement, so a `$&` in a name is not read as a replacement pattern.
    const heading = this.heading.getAttribute('data-heading');
    this.heading.textContent = heading.replace('[product_name]', () => String(productName));
  }

  /**
   * Add to cart
   * @param {Object} data - The data to add to cart
   * @param {Object} data.sku - The SKU splitted by components
   * @param {number} data.quantity - The quantity of the product
   * @param {string} data.configurationId - The configuration ID of the product
   * @param {string} data.thumbnail - The thumbnail of the product
   */
  async addToCart({
    sku,
    quantity,
    configurationId,
    previousConfigurationId,
    uploadKey,
    thumbnail,
    nextImage,
    imageEffect,
  }) {
    this.toggleLoading(true);

    // Start timing if not already started (before_add_to_cart may not have fired with SKU)
    if (!this._perfStart) this._perfStart = performance.now();
    const t0 = this._perfStart;

    try {
      // Lookup the select variant by PM and F (Print Material and Format)
      // Use pre-fetched result if available and SKU matches, otherwise fetch normally
      let variant;
      let lookupSource;
      const skuKey = `${sku?.PM}${sku?.F}`;
      if (this._prefetchPromise && this._prefetchKey === skuKey) {
        variant = await this._prefetchPromise;
        this._prefetchPromise = null;
        this._prefetchKey = null;
        // Fallback if pre-fetch returned null
        if (!variant) {
          lookupSource = 'pre-fetch miss → fresh fetch';
          variant = await productLookUp.find(sku?.PM, sku?.F);
        } else {
          lookupSource = 'pre-fetch hit';
        }
      } else {
        lookupSource = 'fresh fetch (no pre-fetch)';
        variant = await productLookUp.find(sku?.PM, sku?.F);
      }
      PicturatorConfigurator.debug &&
        debugLog(
          `Variant resolved (${lookupSource}): +${Math.round(performance.now() - t0)}ms`,
          variant
        );

      // Check if the variant is available for sale
      if (!variant?.availableForSale) {
        throw new Error(window.toast_translations.cart.no_product);
      }

      // Create the add to cart data
      const formData = await addToCartData.create({
        variant,
        sku,
        quantity,
        configurationId,
        previousConfigurationId,
        uploadKey,
        thumbnail,
        imageEffect,
      });

      PicturatorConfigurator.debug && debugLog('Picturator: Add to cart form data', formData);

      // If update line item then remove the previous line item
      if (this.updateLineItem) {
        const id = previousConfigurationId || configurationId;
        const removeLineItem = await this.getLineItem(id);
        PicturatorConfigurator.debug && debugLog('Picturator: Remove line item', removeLineItem);
        if (removeLineItem) {
          // If the thumbnail is not received from the configurator, set it from the previous line item
          if (
            !formData.items[0].properties?._thumbnail ||
            formData.items[0].properties._thumbnail.includes('data:image')
          ) {
            formData.items[0].properties._thumbnail = removeLineItem.properties._thumbnail;
          }
          await this.removeLineItem(removeLineItem.key);
        }
      }

      // Add to cart fetch
      const response = await fetch(`${routes.cart_add_url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      // Parse the response
      const data = await response.json();
      PicturatorConfigurator.debug && debugLog('Picturator: Add to cart result', data);
      if (!response.ok && response.status === 422) {
        throw new Error(data.message || window.toast_translations.cart.error);
      }
      if (!response.ok) throw new Error(window.toast_translations.cart.error);

      PicturatorConfigurator.debug && debugLog('Picturator: Add to cart result', formData);

      this.onSuccess(nextImage, data);
    } catch (error) {
      this.onError(error);
    } finally {
      PicturatorConfigurator.debug &&
        debugLog(`Phase 2 total: ${Math.round(performance.now() - t0)}ms`);
      this._perfStart = null;
      this.toggleLoading(false);
    }
  }

  /**
   * Get the line item by configuration ID
   * @param {string} configurationId - The configuration ID
   * @returns {Object} - The line item
   */
  async getLineItem(configurationId) {
    if (!configurationId) return;

    // Get cart data
    const response = await fetch(`${routes.cart_url}.js`);

    // if no response, return null
    if (!response.ok) return null;

    // Parse the response
    const cart = await response.json();

    // If cart is empty, return null
    if (!cart.items) return null;

    // Find line item with matching configuration ID
    return cart.items.find((item) => item.properties?._configuration_id === configurationId);
  }

  /**
   * Remove the line item by ID
   * @param {string} lineItemKey - The line item key
   */
  async removeLineItem(lineItemKey) {
    if (!lineItemKey) return;

    const response = await fetch(`${routes.cart_change_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: lineItemKey,
        quantity: 0,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to remove line item from cart');
    }

    return response.json();
  }

  /**
   * Toggle loading state on submit buttons
   * @param {boolean} loading
   */
  toggleLoading(loading) {
    this.classList.toggle('loading', loading);
  }

  /**
   * Get download URL from sessionStorage for a given uploadKey
   * @param {string} uploadKey - The upload key to look up
   * @returns {string|null} - The download URL or null if not found
   */
  #getDownloadUrlFromStorage(uploadKey) {
    if (!uploadKey) return null;

    try {
      const storageKey = `upload-key-${uploadKey}`;
      const storedData = sessionStorage.getItem(storageKey);

      if (!storedData) return null;

      // Check if it's a plain URL string or JSON object
      if (storedData.startsWith('http')) {
        // Plain URL string - use it directly with size=450
        let downloadUrl = storedData;
        if (downloadUrl.includes('size=')) {
          downloadUrl = downloadUrl.replace(/size=\d+/, 'size=450');
        } else {
          downloadUrl = downloadUrl + (downloadUrl.includes('?') ? '&' : '?') + 'size=450';
        }
        return downloadUrl;
      }

      // Try to parse as JSON
      const parsedData = JSON.parse(storedData);
      if (parsedData.previewThumbnail) {
        let downloadUrl = parsedData.previewThumbnail;
        if (downloadUrl.includes('size=')) {
          downloadUrl = downloadUrl.replace(/size=\d+/, 'size=450');
        } else {
          downloadUrl = downloadUrl + (downloadUrl.includes('?') ? '&' : '?') + 'size=450';
        }
        return downloadUrl;
      }
      return parsedData.downloadUrl || null;
    } catch (e) {
      console.warn('⚠️ Failed to get download URL from sessionStorage:', e);
      return null;
    }
  }

  /**
   * Handle form success
   * @param {Object} nextImage - The next image to display
   * @param {Object} data - The data to add to cart
   */
  onSuccess(nextImage, data) {
    if (nextImage) {
      PicturatorConfigurator.debug && debugLog('Picturator: Next image', nextImage);

      // Store the preview URL in sessionStorage for the next page load
      // This ensures the URL is available when the page reloads
      const previewUrl =
        nextImage.downloadUrl ||
        nextImage.previewUrl ||
        nextImage.previewThumbnail ||
        nextImage.thumbnailUrl;
      if (previewUrl && nextImage.uploadKey) {
        try {
          sessionStorage.setItem(`upload-key-${nextImage.uploadKey}`, previewUrl);
        } catch (e) {
          console.warn('⚠️ Failed to store preview URL in sessionStorage:', e);
        }
      }

      // Build URL with next image's uploadKey and reload the page
      // This ensures the configurator properly initializes with the new image
      const params = new URLSearchParams(window.location.search);
      params.set('uploadKey', nextImage.uploadKey);
      window.location.href = `${window.location.pathname}?${params.toString()}`;
      return;
    }
    if (PicturatorConfigurator.debug) {
      debugLog("Picturator: Don't redirect to cart in debug mode");
      return;
    }
    window.location.href = window.routes.cart_url;
  }

  /**
   * Handle form error
   * @param {Error} error - The error object
   */
  onError({ message }) {
    debugError(message);
    notyf.error(message);
  }
}

customElements.define('picturator-configurator-widget', PicturatorConfigurator);
